import { useMemo } from "react";

import { IShapefile, IShapefileNode } from "models/projects";

import { useEditableProjectLayerList } from "components/project/layers/hooks";
import { useProjectContext } from "components/project/projects/context";

import { useProjectShapefilesQuery, useShapefilesListQuery } from "../queries";
import { Shapefile } from "../queries/useShapefilesListQuery";

type ShapefileTreeNode = IShapefileNode | IShapefile;

function collectLineStyles(
  nodes: ShapefileTreeNode[] | undefined,
  map: Record<string, string>
): void {
  if (!nodes) return;
  for (const node of nodes) {
    if ("lineStyle" in node && node.lineStyle && node.shapefileId) {
      map[node.shapefileId] = node.lineStyle;
    }
    if (Array.isArray(node.children) && node.children.length) {
      collectLineStyles(node.children as ShapefileTreeNode[], map);
    }
  }
}

function collectFillPatterns(
  nodes: ShapefileTreeNode[] | undefined,
  map: Record<string, string>
): void {
  if (!nodes) return;
  for (const node of nodes) {
    if ("fillPattern" in node && node.fillPattern && node.shapefileId) {
      map[node.shapefileId] = node.fillPattern as string;
    }
    if (Array.isArray(node.children) && node.children.length) {
      collectFillPatterns(node.children as ShapefileTreeNode[], map);
    }
  }
}

export function useCheckedShapefileList() {
  const { selectedProjectId } = useProjectContext();
  const layerList = useEditableProjectLayerList({ projectId: selectedProjectId });

  const shapefileListQuery = useShapefilesListQuery({
    projectId: selectedProjectId,
    shapefileIds: layerList.checkedProjectShapefileIds,
    projectLinkedShapefileIds: layerList.checkedProjectLinkedShapefileIds
  });

  const projectShapefiles = useProjectShapefilesQuery({
    projectId: selectedProjectId
  });

  const enrichedData = useMemo(() => {
    const shapefiles: Shapefile[] | undefined = shapefileListQuery.data;
    if (!shapefiles?.length) return shapefiles;

    const treeData = projectShapefiles.data;
    if (!treeData) return shapefiles;

    // Build a shapefileId → lineStyle lookup from the project tree
    const lineStyleMap: Record<string, string> = {};
    collectLineStyles(treeData.Project?.children, lineStyleMap);
    collectLineStyles(treeData.Organization?.children, lineStyleMap);
    collectLineStyles(treeData.McDaniel?.children, lineStyleMap);

    // Build a shapefileId → fillPattern lookup from the project tree
    const fillPatternMap: Record<string, string> = {};
    collectFillPatterns(treeData.Project?.children, fillPatternMap);
    collectFillPatterns(treeData.Organization?.children, fillPatternMap);
    collectFillPatterns(treeData.McDaniel?.children, fillPatternMap);

    // Only create new array if any shapefile is missing lineStyle or fillPattern
    const needsEnrichment = shapefiles.some(
      (sf) =>
        (!sf.lineStyle && lineStyleMap[sf.shapefileId]) ||
        (!sf.fillPattern && fillPatternMap[sf.shapefileId])
    );
    if (!needsEnrichment) return shapefiles;

    return shapefiles.map((sf) => {
      const updates: Partial<Shapefile> = {};
      if (!sf.lineStyle && lineStyleMap[sf.shapefileId]) {
        updates.lineStyle = lineStyleMap[sf.shapefileId];
      }
      if (!sf.fillPattern && fillPatternMap[sf.shapefileId]) {
        updates.fillPattern = fillPatternMap[sf.shapefileId];
      }
      return Object.keys(updates).length ? { ...sf, ...updates } : sf;
    });
  }, [shapefileListQuery.data, projectShapefiles.data]);

  return {
    ...shapefileListQuery,
    data: enrichedData
  };
}
